import { useEffect, useState, type FormEvent } from 'react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Button } from '@/components/ui/Button/Button';
import { Field } from '@/components/ui/Field/Field';
import { Icon } from '@/components/ui/Icon/Icon';
import { useUploadGalleryImage } from '@/services/gallery';
import { compressImageForUpload } from '@/utils/compressImage';
import { nb } from '@/i18n/nb';
import s from './GalleryUploadModal.module.css';

/*
 * Upload modal (wireframe upload-modal) — registered_guest+, opened from the grid's
 * "Last opp" action in Rediger-modus. Image + caption are ONE inseparable unit: both
 * are required before submit (the defining Galleri rule — contrast Vedlikehold, where
 * the image is an optional separable attachment). Picked photos are re-encoded
 * client-side to fit the effective upload limit (compressImageForUpload, ADR-0030)
 * before they land in state, so oversized originals become uploads that just work.
 * The server stays authoritative on MIME / size / dimension caps; the client-side
 * checks below are the safety net for files compression couldn't touch (e.g. HEIC
 * in a browser that can't decode it — fail-open passes the original through).
 */

// Client-side mirror of the server caps (ImagePipelineService) for instant, accurate errors.
const MAX_BYTES = 20 * 1024 * 1024;
const MAX_DIMENSION = 6000;

export function GalleryUploadModal({ onClose }: { onClose: () => void }) {
  const [image, setImage] = useState<File | null>(null);
  const [caption, setCaption] = useState('');
  const [errors, setErrors] = useState<{ image?: string; caption?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  // True while a picked photo is being decoded + re-encoded — gates submit so the
  // form can't post a half-prepared (or missing) image.
  const [isPreparing, setIsPreparing] = useState(false);
  const upload = useUploadGalleryImage();

  // Object URL for the local preview; revoked on change/unmount to avoid a leak.
  const [preview, setPreview] = useState<string | null>(null);
  // Intrinsic dimensions for the client-side cap check, read from the decoded image.
  // Stays null for HEIC (browsers can't decode it) — the server pipeline enforces the
  // dimension cap in that fallback case.
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);
  useEffect(() => {
    if (!image) {
      setPreview(null);
      setDimensions(null);
      return;
    }
    const url = URL.createObjectURL(image);
    setPreview(url);
    setDimensions(null);
    const probe = new Image();
    probe.onload = () => setDimensions({ width: probe.naturalWidth, height: probe.naturalHeight });
    probe.src = url;
    return () => URL.revokeObjectURL(url);
  }, [image]);

  function validate(): boolean {
    const next: typeof errors = {};
    if (!image) {
      next.image = nb.galleri.uploadModal.validation.imageRequired;
    } else if (image.size > MAX_BYTES) {
      next.image = nb.galleri.uploadModal.validation.imageTooLarge;
    } else if (dimensions && (dimensions.width > MAX_DIMENSION || dimensions.height > MAX_DIMENSION)) {
      next.image = nb.galleri.uploadModal.validation.imageTooBig;
    }
    const trimmed = caption.trim();
    if (trimmed.length === 0) next.caption = nb.galleri.uploadModal.validation.captionRequired;
    else if (trimmed.length > 256) next.caption = nb.galleri.uploadModal.validation.captionMax;
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitError(null);
    if (isPreparing || !validate() || !image) return;

    upload.mutate(
      { image, caption: caption.trim() },
      {
        onSuccess: () => onClose(),
        onError: (e) =>
          setSubmitError(e instanceof Error ? e.message : nb.galleri.uploadModal.errorTitle),
      },
    );
  }

  // Compress oversized picks before they land in state — the preview, the
  // client-side validation, and the eventual POST all see the prepared file.
  async function pickFile(file: File | null) {
    if (!file) {
      setImage(null);
      return;
    }
    setErrors((prev) => ({ ...prev, image: undefined }));
    setIsPreparing(true);
    try {
      setImage(await compressImageForUpload(file));
    } finally {
      setIsPreparing(false);
    }
  }

  return (
    <Modal open={true} onClose={onClose} title={nb.galleri.uploadModal.title}>
      <form className={s.form} onSubmit={handleSubmit} noValidate>
        <div className={s.imageSection}>
          <span className={s.label}>{nb.galleri.uploadModal.imageLabel}</span>

          {preview ? (
            <img src={preview} alt="" className={s.preview} />
          ) : (
            <label className={s.dropbox} data-invalid={errors.image ? 'true' : undefined}>
              <Icon name="upload" size={26} />
              <span>{nb.galleri.uploadModal.imagePick}</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/heic"
                onChange={(e) => void pickFile(e.target.files?.[0] ?? null)}
                className={s.fileInputHidden}
              />
            </label>
          )}

          {preview && (
            <label className={s.uploadButton}>
              <Icon name="upload" size={16} /> {nb.galleri.uploadModal.imageReplace}
              <input
                type="file"
                accept="image/jpeg,image/png,image/heic"
                onChange={(e) => void pickFile(e.target.files?.[0] ?? null)}
                className={s.fileInputHidden}
              />
            </label>
          )}

          <span className={s.formatNote}>
            {isPreparing ? nb.galleri.uploadModal.preparing : nb.galleri.uploadModal.formatNote}
          </span>
          {errors.image && (
            <span role="alert" className={s.errorMsg}>
              {errors.image}
            </span>
          )}
        </div>

        <Field label={nb.galleri.uploadModal.captionLabel} error={errors.caption}>
          {(wiring) => (
            <>
              <textarea
                {...wiring}
                className={`${s.input} ${s.textarea}`}
                rows={3}
                maxLength={256}
                value={caption}
                placeholder={nb.galleri.uploadModal.captionPlaceholder}
                onChange={(e) => setCaption(e.target.value)}
              />
              <span className={s.counter}>{caption.length} / 256</span>
            </>
          )}
        </Field>

        {submitError && (
          <p role="alert" className={s.submitError}>
            {submitError}
          </p>
        )}

        <div className={s.actions}>
          <Button variant="secondary" type="button" onClick={onClose}>
            {nb.common.cancel}
          </Button>
          <Button
            variant="primary"
            type="submit"
            loading={upload.isPending}
            disabled={isPreparing}
          >
            {nb.galleri.uploadModal.submit}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
